/**
 * Does this object key belong to this patient?
 *
 * Keys are chosen by the server on upload — `patients/<id>/strip/<id>.jpg` —
 * but they travel back through the client whenever a photo is attached to a
 * medication. `patients/somebody-else/strip/abc.jpg` is a perfectly
 * well-formed string, and the media route authorises a key against the
 * patient named *in the key*: so a medication pointed at another patient's
 * photograph would be served that photograph quite happily.
 *
 * The check is one line and therefore exactly the kind of thing that gets
 * written in one caller and forgotten in the next. It lives here with tests,
 * because the failure is silent and the consequence is somebody else's
 * prescription.
 */

/** The kinds of photo DrBell stores. Mirrors the media route's `KINDS`. */
// `pack` is the front of the box or bottle (RX-MED-12). Missing here, every
// front-of-pack photo was refused as "not this patient's" -- found taking the
// store screenshots.
export const MEDIA_KINDS = ['pill', 'strip', 'pack', 'prescription', 'avatar'] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

const KEY = new RegExp(
  `^patients/([A-Za-z0-9_-]+)/(${MEDIA_KINDS.join('|')})/[A-Za-z0-9_.-]+$`,
);

/** The patient a key names, or null if it is not a key we would ever issue. */
export function patientFromMediaKey(key: string): string | null {
  return KEY.exec(key)?.[1] ?? null;
}

export function mediaKeyBelongsTo(key: string, patientId: string): boolean {
  /*
   * Parsed, not prefix-matched.
   *
   * `key.startsWith(\`patients/${patientId}/\`)` looks equivalent and is not:
   * it accepts `patients/p1/../p2/strip/x.jpg`, and it accepts a patient id
   * that is a prefix of another one if the trailing slash is ever dropped.
   * Matching the whole shape leaves nothing to reason about.
   */
  return patientFromMediaKey(key) === patientId;
}
